import { ChangeDetectorRef, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { ExposedReadUserDetailsRes } from 'app/interfaces/responses/Exposed/exposedReadUserDetailsRes';
import { LsrauthService } from 'app/services/api/lsrauth/lsrauth.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { USER_ROLES } from 'common/constants/user-role.constants';
import { Observable, Subject, combineLatest, filter, takeUntil } from 'rxjs';

@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
})
export class DocumentsComponent implements OnInit {
    //#region Keypresses and before leave

    @HostListener('window:beforeunload')
    canDeactivate(): Observable<boolean> | boolean {
        try {
            let hasNoFormChanges = false;
            return !hasNoFormChanges;
        } catch (e) {
            return true;
        }
    }

    //#endregion

    //#region Globals

    showPanel = false;
    userDetails: ExposedReadUserDetailsRes = null;
    beforeLeaveMessage = this.translationService.translate('info_leave_form_message');
    panels: any[] = [];
    selectedPanel: string = 'skoda-skjol';
    selectedComponent: string = 'app-documents-overview';

    //#endregion

    //#region Init

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private route: ActivatedRoute,
        private router: Router,
        private translationService: TranslationService,
        public lsrauthService: LsrauthService
    ) {
        this.lsrauthService.userDetails.subscribe((userDetails) => (this.userDetails = userDetails));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.initPanel();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    //#endregion

    //#region FUSE

    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('drawer') drawer: MatDrawer;
    drawerMode: 'over' | 'side' = 'side';
    drawerOpened: boolean = true;

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    //#endregion

    //#region Panel

    initPanel() {
        this.selectedPanel = this.route.snapshot.paramMap.get('panel');

        combineLatest([this.lsrauthService.fetchUserDetails(true)]).subscribe(([userProfile]) => {
            let canAddDocument =
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.DOCUMENT_CENTER_SUPERUSER) ||
                this.lsrauthService.inRole(this.userDetails, USER_ROLES.UPLOAD_DOCUMENTS);
            let canViewJobs = this.lsrauthService.inRole(this.userDetails, USER_ROLES.VIEW_JOB_STATUS_ROLE);

            if (this.selectedPanel == null && !canAddDocument) {
                this.selectedPanel = 'skoda-skjol';
                this.goToPanel('skoda-skjol', 'app-documents-overview');
            } else if (this.selectedPanel == null && canAddDocument) {
                this.selectedPanel = 'skra-skjal';
                this.goToPanel('skra-skjal', 'app-documents-add');
            }

            let viewDocumentPanel = {
                id: 'skoda-skjol',
                component: 'app-documents-overview',
                icon: 'heroicons_outline:document-search',
                title: 'Skoða skjöl',
                description: 'Hér finnur þú þín skjöl',
                disabled: true,
            };
            this.panels.push(viewDocumentPanel);

            if (canAddDocument) {
                let addDocumentPanel = {
                    id: 'skra-skjal',
                    component: 'app-documents-add',
                    icon: 'heroicons_outline:document-add',
                    title: 'Skrá skjal',
                    description: 'Hér skráir þú nýtt skjal',
                    disabled: true,
                };
                this.panels.unshift(addDocumentPanel);
            }

            if (canViewJobs) {
                let viewDocumentJobsPanel = {
                    id: 'skoda-bunka-vinnslur',
                    component: 'documents-jobs',
                    icon: 'heroicons_outline:cog',
                    title: 'Skoða bunkavinnslur',
                    description: 'Hér finnur þú þínar bunkavinnslur',
                    disabled: true,
                };
                this.panels.push(viewDocumentJobsPanel);
            }

            this.selectedComponent = this.panels.find((x) => x.id == this.selectedPanel).component;

            // Subscribe to media changes
            this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened
                if (matchingAliases.includes('lg')) {
                    this.drawerMode = 'side';
                    this.drawerOpened = true;
                } else {
                    this.drawerMode = 'over';
                    this.drawerOpened = false;
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

            this.router.events.pipe(filter((event: any) => event instanceof NavigationEnd)).subscribe((x) => {
                try {
                    this.selectedPanel = this.route.snapshot.paramMap.get('panel');
                    this.selectedComponent = this.panels.find((x) => x.id == this.route.snapshot.paramMap.get('panel')).component;
                } catch {}
            });

            this.showPanel = true;
        });
    }

    /**
     * Navigate to the panel. Checks if something has been editied within the associated forms.
     *
     * @param panel
     */
    goToPanel(panel: string, component: string): void {
        this.proceedPanel(panel, component);
    }

    proceedPanel(panel: string, component: string): void {
        this.router.navigate(['/skjalasafn/' + panel]);
        this.clearTouchForms();
        this.selectedPanel = panel;
        this.selectedComponent = component;

        // Close the drawer on 'over' mode
        if (this.drawerMode === 'over') {
            this.drawer.close();
        }
    }

    // When switching between panels we mark all forms as untouched.
    clearTouchForms() {}

    /**
     * Get the details of the panel
     *
     * @param id
     */
    getPanelInfo(id: string): any {
        return this.panels.find((panel) => panel.id === id);
    }

    getPanelTitle(id: string): any {
        try {
            return this.panels.find((panel) => panel.id === id).title;
        } catch {
            return '';
        }
    }

    getPanelDescription(id: string): any {
        try {
            return this.panels.find((panel) => panel.id === id).description;
        } catch {
            return '';
        }
    }

    //#endregion
}
